/**
 * Rule-based Similar Items cascade engine.
 *
 * Implements a cascading filter that tries to match on the maximum number
 * of attributes first, then progressively relaxes by dropping the
 * lowest-priority attribute until the target number of recommendations is
 * filled. The attribute priority lists (`JEWELLERY_ATTRS`, `WATCH_ATTRS`)
 * and slot targets are defined below.
 */

import type {
  Category,
  JewelleryAttribute,
  SlimProduct,
  WatchAttribute,
} from './types'

const JEWELLERY_TYPES = new Set<string>([
  'earrings',
  'rings',
  'pendants',
  'bracelets',
  'necklets',
  'bangles',
  'charms',
  'chains',
  'religious-medals',
  'locket',
  'anklets',
  'body-jewellery',
  'brooches',
])

const WATCH_TYPES = new Set<string>(['watches', 'smart-watches'])

// Priority order: highest to lowest. The cascade drops from the end.
const JEWELLERY_ATTRS: JewelleryAttribute[] = [
  'gender',
  'brand',
  'material',
  'gemstone',
  'feature',
  'style',
]

const WATCH_ATTRS: WatchAttribute[] = ['gender', 'brand', 'material']

const JEWELLERY_SLOTS = 8
const WATCH_SIMILAR_SLOTS = 5
const WATCH_UPSELL_SLOTS = 3

type AnyAttr = JewelleryAttribute | WatchAttribute

/**
 * Normalise a Shopify productType for use as a Map key or category lookup.
 * Lowercased, with underscores and whitespace collapsed to hyphens. Exported
 * so the cron and the cascade engine agree on the same key shape — drift
 * here would split type pools and silently produce empty recommendations.
 */
export function normaliseType(raw?: string): string {
  return (raw ?? '')
    .toLowerCase()
    .replace(/_/g, '-')
    .replace(/\s+/g, '-')
    .trim()
}

export function getCategory(productType?: string): Category | null {
  const key = normaliseType(productType)
  if (JEWELLERY_TYPES.has(key)) return 'jewellery'
  if (WATCH_TYPES.has(key)) return 'watches'
  return null
}

/**
 * Derive a normalised metal colour string from either a material value or
 * `metal_colour:` / `metal_color:` tags. Shared between the cron, API
 * routes, and the SimilarItems fallback request so the scoring input is
 * consistent no matter where the source product is prepared.
 */
export function deriveMetalColour(
  material: string | undefined,
  tags: string[] | undefined
): string {
  const fromString = (s?: string) => {
    if (!s) return ''
    const l = s.toLowerCase()
    if (l.includes('rose') || l.includes('pink')) return 'rose'
    if (l.includes('yellow')) return 'yellow'
    if (l.includes('white')) return 'white'
    if (l.includes('two-tone') || l.includes('two tone')) return 'two-tone'
    // Multi-tone deliberately maps to '' so it doesn't constrain matching
    // to a single colour — multi-tone pieces should be eligible against
    // any colour bucket via cascade fallback.
    if (l.includes('multi-tone') || l.includes('multi tone')) return ''
    if (l.includes('platinum')) return 'platinum'
    if (l.includes('silver')) return 'silver'
    return ''
  }
  const fromMat = fromString(material)
  if (fromMat) return fromMat
  for (const tag of tags || []) {
    const lower = tag.toLowerCase()
    if (lower.startsWith('metal_colour:') || lower.startsWith('metal_color:')) {
      return fromString(tag.slice(tag.indexOf(':') + 1).trim())
    }
  }
  return ''
}

/**
 * Parse the `custom.gender` metafield value. Shopify list-type metafields
 * serialise as a JSON array string; older single-value entries may be a raw
 * string. Shared between the cron and the runtime API routes so they all
 * normalise gender the same way.
 */
export function parseGender(raw?: string): string[] {
  if (!raw) return []
  try {
    const parsed = JSON.parse(raw)
    if (Array.isArray(parsed)) return parsed.map(String)
  } catch (_) {}
  return [raw]
}

function getAttrValues(p: SlimProduct, attr: AnyAttr): string[] {
  switch (attr) {
    case 'gender':
      return p.namedMetafields.gender.filter(Boolean)
    case 'brand':
      return p.namedMetafields.brand ? [p.namedMetafields.brand] : []
    case 'material':
      return p.namedMetafields.material ? [p.namedMetafields.material] : []
    case 'gemstone':
      return p.namedMetafields.gemstone ? [p.namedMetafields.gemstone] : []
    case 'style':
      return p.namedMetafields.style ? [p.namedMetafields.style] : []
    case 'feature':
      return p.featureTags
    default:
      return []
  }
}

const OVERLAP_ATTRS = new Set<AnyAttr>(['gender', 'feature'])

export function attributeMatches(
  source: SlimProduct,
  candidate: SlimProduct,
  attr: AnyAttr
): boolean {
  const a = getAttrValues(source, attr)
  const b = getAttrValues(candidate, attr)
  if (a.length === 0 || b.length === 0) return false

  if (OVERLAP_ATTRS.has(attr)) {
    const bl = new Set(b.map((v) => v.toLowerCase()))
    return a.some((v) => bl.has(v.toLowerCase()))
  }
  // Exact, case-insensitive
  return a[0].toLowerCase() === b[0].toLowerCase()
}

function scoreCandidate(
  source: SlimProduct,
  candidate: SlimProduct,
  attrs: AnyAttr[]
): number {
  let score = 0
  for (const attr of attrs) {
    if (attributeMatches(source, candidate, attr)) score++
  }
  return score
}

function cascadeLevels(attrs: AnyAttr[]): AnyAttr[][] {
  // Level 1 requires all attributes, level 2 drops the last (lowest priority),
  // etc., down to level N which requires only the first. Category-only (no
  // attribute requirements) is handled by the final empty array level.
  const levels: AnyAttr[][] = []
  for (let i = attrs.length; i > 0; i--) {
    levels.push(attrs.slice(0, i))
  }
  levels.push([]) // category-only fallback
  return levels
}

/**
 * Core cascade loop. Fills up to `limit` slots from `pool` using cascading
 * attribute requirements. Skips handles already in `excludeHandles`.
 *
 * When `useScoring` is true (jewellery), candidates within each level are
 * ranked by total attribute score → metal-colour match → price proximity.
 * When false (watches), candidates fill in catalog order.
 */
function cascadeFill(
  source: SlimProduct,
  pool: SlimProduct[],
  attrs: AnyAttr[],
  limit: number,
  excludeHandles: Set<string>,
  useScoring: boolean
): SlimProduct[] {
  const selected: SlimProduct[] = []
  const selectedHandles = new Set(excludeHandles)
  const levels = cascadeLevels(attrs)

  for (const requiredAttrs of levels) {
    if (selected.length >= limit) break

    const passing: SlimProduct[] = []
    for (const candidate of pool) {
      if (selectedHandles.has(candidate.handle)) continue
      const ok = requiredAttrs.every((a) =>
        attributeMatches(source, candidate, a)
      )
      if (ok) passing.push(candidate)
    }

    if (useScoring && passing.length > 1) {
      // Within-level ranking:
      //   1. score desc — total attrs matched against the full attribute list
      //   2. metal-colour match desc — prefer same colour
      //   3. price proximity asc — prefer closer price band
      //   4. catalog index asc — stable tiebreaker
      const sourceColour = source.metalColour?.toLowerCase() ?? ''
      const sourcePrice = source.price ?? 0
      const scored = passing.map((c, idx) => {
        const colourMatch =
          sourceColour && c.metalColour
            ? c.metalColour.toLowerCase() === sourceColour
              ? 1
              : 0
            : 0
        return {
          c,
          idx,
          s: scoreCandidate(source, c, attrs),
          colourMatch,
          priceDelta: Math.abs((c.price ?? 0) - sourcePrice),
        }
      })
      scored.sort(
        (a, b) =>
          (b.s - a.s) ||
          (b.colourMatch - a.colourMatch) ||
          (a.priceDelta - b.priceDelta) ||
          (a.idx - b.idx)
      )
      for (const { c } of scored) {
        if (selected.length >= limit) break
        if (selectedHandles.has(c.handle)) continue
        selected.push(c)
        selectedHandles.add(c.handle)
      }
    } else {
      for (const c of passing) {
        if (selected.length >= limit) break
        if (selectedHandles.has(c.handle)) continue
        selected.push(c)
        selectedHandles.add(c.handle)
      }
    }
  }

  return selected
}

export function getSimilarProducts(
  source: SlimProduct,
  catalog: SlimProduct[]
): SlimProduct[] {
  const category = getCategory(source.productType)
  if (!category) return []

  const attrs = category === 'jewellery' ? JEWELLERY_ATTRS : WATCH_ATTRS
  const requireInStock = category === 'jewellery'
  const useScoring = category === 'jewellery'
  const sourceType = normaliseType(source.productType)

  // Build candidate pool: same product type, exclude self, stock filter.
  const pool: SlimProduct[] = []
  for (const candidate of catalog) {
    if (!candidate) continue
    if (candidate.handle === source.handle) continue
    if (normaliseType(candidate.productType) !== sourceType) continue
    if (requireInStock && !candidate.inStock) continue
    pool.push(candidate)
  }

  if (pool.length === 0) return []

  const similarLimit = category === 'jewellery' ? JEWELLERY_SLOTS : WATCH_SIMILAR_SLOTS
  const similar = cascadeFill(source, pool, attrs, similarLimit, new Set(), useScoring)

  if (category !== 'watches') return similar

  // Watches: append up to 3 higher-priced upsell products using the same
  // cascade logic, restricted to candidates priced above the source and
  // excluding already-selected similar items.
  const sourcePrice = source.price ?? 0
  const upsellPool = pool.filter((c) => (c.price ?? 0) > sourcePrice)
  const alreadySelected = new Set(similar.map((s) => s.handle))
  const upsell = cascadeFill(source, upsellPool, attrs, WATCH_UPSELL_SLOTS, alreadySelected, false)

  return [...similar, ...upsell]
}
